import {
  CommonActions,
  NavigationProp,
  useIsFocused,
  useLinkTo,
  useNavigation,
} from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import Constants from 'expo-constants';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import React, { useCallback, useEffect } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useLogInMutation } from 'services/backend';
import { RootState } from 'store/index';
import { setShowBottomBar } from 'store/slices/profileSlice';
import { clearServerSession } from 'utils/clearServerSession';
import { parseUrl } from 'utils/index';
import { Platform } from 'utils/platform';

import CTVLoginScreen from './index.ctv';
import { BackGroundImageView } from './styles';

import Logo from 'assets/loginLogo.png';

import NewGradientButton from 'components/Button/NewGradienteButton';
import IsLoading from 'components/isLoading';

WebBrowser.maybeCompleteAuthSession();

const ImageGrid = (props: { LoginScreen?: LoginScreenImage }) => {
  const { LoginScreen } = props;

  return (
    <View style={styles.imageGridContainer}>
      <Image
        style={styles.imageSmall}
        accessibilityLabel={LoginScreen?.loginImage_6?.title}
        source={{ uri: LoginScreen?.loginImage_6?.url }}
      />
      <Image
        style={styles.imageMedium}
        accessibilityLabel={LoginScreen?.loginImage_2?.title}
        source={{ uri: LoginScreen?.loginImage_2?.url }}
      />
      <Image
        style={styles.imageLarge}
        accessibilityLabel={LoginScreen?.loginImage_1?.title}
        source={{ uri: LoginScreen?.loginImage_1?.url }}
      />
      <Image
        style={styles.imageMedium}
        accessibilityLabel={LoginScreen?.loginImage_5?.title}
        source={{ uri: LoginScreen?.loginImage_5?.url }}
      />
      <Image
        style={styles.imageLarge}
        accessibilityLabel={LoginScreen?.loginImage_3?.title}
        source={{ uri: LoginScreen?.loginImage_3?.url }}
      />
      <Image
        style={styles.imageExtraSmall}
        accessibilityLabel={LoginScreen?.loginImage_4?.title}
        source={{ uri: LoginScreen?.loginImage_4?.url || `` }}
      />
    </View>
  );
};

const LoginScreenComponent: React.FC<RootStackScreenProps<'Login'>> = (props) => {
  const { route } = props;
  const linkTo = useLinkTo();
  const analytics = useAnalytics();
  const [logIn] = useLogInMutation();
  const { LoginScreen, dataIsLoading } = useGlobalConfig();
  const [isLoginLoading, setIsLoginLoading] = React.useState(false);
  const { loginBaseUrl, loginClientId } = Constants.expoConfig?.extra || {};
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setShowBottomBar({ showBottomBar: false }));
    return () => {
      dispatch(setShowBottomBar({ showBottomBar: true }));
    };
  }, []);

  const handleLogin = (prefer: 'signup' | 'signin' = 'signin') => {
    return async () => {
      clearServerSession();
      if (prefer === 'signin') analytics.triggerLoginActionEvent();
      else analytics.triggerCreateAccountActionEvent();

      const redirectUri = Linking.createURL('/entrar');
      const encodedUri = encodeURIComponent(redirectUri);

      const loginUrl = `${loginBaseUrl}/${
        prefer === 'signup' ? 'signup?' : ''
      }authorize?client_id=${loginClientId}&response_type=code&redirect_uri=${encodedUri}&prefer=${prefer}&session=strict`;

      const res = await WebBrowser.openAuthSessionAsync(loginUrl, redirectUri);

      if (res.type === 'success') {
        const { query } = parseUrl(res.url);
        const { code } = query;
        setIsLoginLoading(true);

        try {
          await logIn({ code }).unwrap().catch(console.error);
          if (route.params?.redirect) linkTo(route.params.redirect);
          else linkTo('/');
        } finally {
          // dispatch(setShowBottomBar({ showBottomBar: true }));
          setIsLoginLoading(false);
        }
      }
    };
  };

  if (!LoginScreen || dataIsLoading || isLoginLoading) {
    return (
      <BackGroundImageView source={{ uri: LoginScreen?.loginBg?.url }}>
        <View style={styles.loadingContainer}>
          <IsLoading />
        </View>
      </BackGroundImageView>
    );
  }

  return (
    <View style={styles.container}>
      <BackGroundImageView source={{ uri: LoginScreen?.loginBg?.url }}>
        <View style={styles.contentContainer}>
          <Image source={Logo} style={styles.logo} accessibilityLabel="Bandplay" />
          <ImageGrid LoginScreen={LoginScreen} />

          <View style={styles.bottomSection}>
            <Text style={styles.descriptionText}>{LoginScreen?.description}</Text>

            <View style={styles.buttonsContainer}>
              <NewGradientButton height={54} onPress={handleLogin()} width="$full">
                <Text style={styles.loginButtonText}>Já tenho conta</Text>
              </NewGradientButton>

              <TouchableOpacity style={styles.signupButton} onPress={handleLogin('signup')}>
                <Text style={styles.signupButtonText}>Criar conta gratuita</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </BackGroundImageView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#161233',
  },
  contentContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 370,
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  loadingContainer: {
    backgroundColor: 'transparent',
    flex: 1,
    height: '100%',
    width: '100%',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageGridContainer: {
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  imageSmall: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  imageMedium: {
    width: 100,
    height: 100,
    borderRadius: 8,
  },
  imageLarge: {
    width: 100,
    height: 150,
    borderRadius: 8,
  },
  imageExtraSmall: {
    width: 85,
    height: 85,
    borderRadius: 8,
  },
  logo: {
    width: 200,
    height: 50,
  },
  bottomSection: {
    width: '100%',
    marginVertical: 32,
    alignItems: 'center',
  },
  descriptionText: {
    color: 'white',
    fontSize: 20,
    lineHeight: 28,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  buttonsContainer: {
    width: '100%',
    marginTop: 16,
  },
  loginButtonText: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#070d31',
    fontSize: 16,
  },
  signupButton: {
    height: 54,
    borderWidth: 2,
    backgroundColor: 'transparent',
    borderColor: '#2240ff',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  signupButtonText: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: 'white',
    fontSize: 16,
  },
});

export default function (props: any) {
  const isFocused = useIsFocused();
  const analytics = useAnalytics();
  const [hardResetKey, setHardResetKey] = React.useState<number>();
  const user = useSelector((state: RootState) => state.auth);
  const navigation = useNavigation<NavigationProp<any>>();

  React.useEffect(() => {
    setHardResetKey(new Date().getTime());
  }, [isFocused]);

  React.useEffect(() => {
    if (user.isAuthenticated && isFocused) {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'Home' }],
        })
      );

      if (user.id) {
        analytics.triggerSetUserId(user.id);
      }
    }
  }, [user, navigation, isFocused]);

  return Platform.isTV ? (
    <CTVLoginScreen key={hardResetKey} {...props} />
  ) : (
    <LoginScreenComponent key={hardResetKey} {...props} />
  );
}
